import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { DiffLineType, checkDiffLineIncludeChange, SplitSide, getSplitContentLines, getUnifiedContentLine, _cacheMap, setEnableBuildTemplate, DiffFile, getFile } from '@git-diff-view/core';
export * from '@git-diff-view/core';
export { SplitSide } from '@git-diff-view/core';
import { useStdout, measureElement, Box, Text } from 'ink';
import * as React from 'react';
import { createContext, useContext, useState, useLayoutEffect, useRef, useEffect, useCallback, memo, useMemo, Fragment as Fragment$1, forwardRef, useImperativeHandle } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim/index.js';
import chalk from 'chalk';
import stringWidth from 'string-width';
import { configureEnv, createStore, ref, markRaw } from 'reactivity-store';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
const memoFunc = (func) => {
    const cache = {};
    return ((key) => {
        if (cache[key]) {
            return cache[key];
        }
        const result = func(key);
        cache[key] = result;
        return result;
    });
};

var NewLineSymbol;
(function (NewLineSymbol) {
    NewLineSymbol[NewLineSymbol["CRLF"] = 1] = "CRLF";
    NewLineSymbol[NewLineSymbol["CR"] = 2] = "CR";
    NewLineSymbol[NewLineSymbol["LF"] = 3] = "LF";
    NewLineSymbol[NewLineSymbol["NEWLINE"] = 4] = "NEWLINE";
    NewLineSymbol[NewLineSymbol["NORMAL"] = 5] = "NORMAL";
    NewLineSymbol[NewLineSymbol["NULL"] = 6] = "NULL";
})(NewLineSymbol || (NewLineSymbol = {}));
var DiffModeEnum;
(function (DiffModeEnum) {
    // github like
    DiffModeEnum[DiffModeEnum["SplitGitHub"] = 1] = "SplitGitHub";
    // gitlab like
    DiffModeEnum[DiffModeEnum["SplitGitLab"] = 2] = "SplitGitLab";
    DiffModeEnum[DiffModeEnum["Split"] = 3] = "Split";
    DiffModeEnum[DiffModeEnum["Unified"] = 4] = "Unified";
})(DiffModeEnum || (DiffModeEnum = {}));

const DiffViewContext = createContext(null);
DiffViewContext.displayName = "DiffViewContext";
const useDiffViewContext = () => useContext(DiffViewContext);

const TERMINAL_PADDING_X = 4;
const safeGetProcessColumn = () => {
    if (typeof process === "object") {
        return process?.stdout?.columns;
    }
    return 0;
};
const getValidColumns = (columns) => {
    if (columns % 2 === 0) {
        return columns;
    }
    else {
        return columns - 1; // Ensure even number of columns
    }
};
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
const debounce = (action, time) => {
    let id = null;
    return ((...args) => {
        if (id)
            clearTimeout(id);
        id = setTimeout(() => action.call(null, ...args), time);
    });
};
function useTerminalSize() {
    const { useDiffContext } = useDiffViewContext();
    const { stdout } = useStdout();
    const wrapper = useDiffContext((s) => s.wrapper);
    const [size, setSize] = useState(() => getValidColumns((safeGetProcessColumn() || stdout?.columns || 60) - TERMINAL_PADDING_X));
    useDiffContext.useShallowStableSelector((s) => s.width, (p, c) => typeof p === typeof c);
    const hasWidth = typeof useDiffContext.getReadonlyState().width === "number";
    useLayoutEffect(() => {
        if (hasWidth)
            return;
        function updateSize() {
            const terminalWidth = getValidColumns((safeGetProcessColumn() || stdout?.columns || 60) - TERMINAL_PADDING_X);
            let width = terminalWidth;
            if (wrapper.current) {
                width = getValidColumns(measureElement(wrapper.current).width);
            }
            width = Math.min(width, terminalWidth);
            setSize(width);
        }
        const debounceUpdate = debounce(updateSize, 200);
        stdout?.on("resize", debounceUpdate);
        return () => {
            stdout?.off("resize", debounceUpdate);
        };
    }, [wrapper, hasWidth, stdout]);
    return { columns: size };
}

const useUnmount = (cb, deps) => {
    const ref = useRef(cb);
    // eslint-disable-next-line react-hooks/refs
    ref.current = cb;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => ref.current, deps);
};

/**
 * Utility for building ANSI-styled strings for proper character-level wrapping in Ink.
 *
 * When using multiple <Text> components inside a <Box flexWrap="wrap">, Yoga layout
 * treats each <Text> as a flex item and tries to keep them together. This causes
 * word-level wrapping instead of character-level wrapping.
 *
 * By building a single string with ANSI escape codes, Ink's wrap-ansi (with hard: true)
 * will properly break at character boundaries.
 *
 * Uses:
 * - chalk for ANSI code generation - cleaner and more maintainable than raw escape codes
 * - string-width for accurate character width calculation (handles CJK, emoji, etc.)
 */
function styleText(text, style) {
    if (!style)
        return text;
    let styledChalk = chalk;
    if (style.backgroundColor) {
        styledChalk = styledChalk.bgHex(style.backgroundColor);
    }
    if (style.color) {
        styledChalk = styledChalk.hex(style.color);
    }
    if (style.dim) {
        styledChalk = styledChalk.dim;
    }
    return styledChalk(text);
}
/**
 * Optimized version that batches consecutive characters with the same style.
 * More efficient for long strings with few style changes.
 */
function buildAnsiStringOptimized(chars) {
    if (chars.length === 0)
        return "";
    const segments = [];
    let currentSegment = { text: "", style: chars[0]?.style };
    for (const { char, style } of chars) {
        const sameStyle = currentSegment.style?.color === style?.color &&
            currentSegment.style?.backgroundColor === style?.backgroundColor &&
            currentSegment.style?.dim === style?.dim;
        if (sameStyle) {
            currentSegment.text += char;
        }
        else {
            if (currentSegment.text) {
                segments.push(currentSegment);
            }
            currentSegment = { text: char, style };
        }
    }
    if (currentSegment.text) {
        segments.push(currentSegment);
    }
    return segments.map((segment) => styleText(segment.text, segment.style)).join("");
}
/**
 * Build ANSI string with manual line breaks at exact character boundaries.
 * This bypasses wrap-ansi's word-boundary logic by pre-inserting newlines.
 *
 * @param chars - Array of characters with styles
 * @param width - Maximum width per line (in characters)
 * @returns ANSI string with newlines inserted at width boundaries
 */
function buildAnsiStringWithLineBreaks(chars, width) {
    if (chars.length === 0 || width <= 0)
        return "";
    // Split into lines at width boundaries
    const lines = [];
    let currentLine = [];
    let currentWidth = 0;
    for (const charData of chars) {
        // Get the display width of the character using string-width
        // Handles CJK, emoji, ANSI codes, and other edge cases correctly
        const charWidth = stringWidth(charData.char);
        if (currentWidth + charWidth > width && currentLine.length > 0) {
            // Start a new line
            lines.push(currentLine);
            currentLine = [];
            currentWidth = 0;
        }
        currentLine.push(charData);
        currentWidth += charWidth;
    }
    // Don't forget the last line
    if (currentLine.length > 0) {
        lines.push(currentLine);
    }
    // Build each line as an ANSI string and join with newlines
    return lines.map((line) => buildAnsiStringOptimized(line)).join("\n");
}
function padRow(row, currentWidth, width, padStyle) {
    const remaining = width - currentWidth;
    if (remaining > 0) {
        return [...row, { char: " ".repeat(remaining), style: padStyle }];
    }
    return row;
}
/**
 * Split styled chars into rows of at most `width` display-columns.
 * Each row is padded with bg-styled spaces to exactly `width`.
 */
function splitCharsIntoRows(chars, width, padStyle) {
    const rows = [];
    let currentRow = [];
    let currentWidth = 0;
    for (const ch of chars) {
        const w = stringWidth(ch.char);
        if (currentWidth + w > width && currentRow.length > 0) {
            rows.push(padRow(currentRow, currentWidth, width, padStyle));
            currentRow = [];
            currentWidth = 0;
        }
        currentRow.push(ch);
        currentWidth += w;
    }
    rows.push(padRow(currentRow, currentWidth, width, padStyle));
    return rows;
}
/**
 * Create a styled block with consistent background across all rows.
 * Each row has fixed width, filled with spaces if needed.
 *
 * @param text - Text to display on first row
 * @param width - Width of each row
 * @param height - Number of rows
 * @param style - Style to apply
 * @param align - Text alignment ('left' | 'right')
 * @returns ANSI string with newlines
 */
function buildStyledBlock(text, width, height, style, align = "left") {
    const lines = [];
    const paddedText = align === "right" ? text.padStart(width) : text.padEnd(width);
    for (let row = 0; row < height; row++) {
        const lineText = row === 0 ? paddedText : " ".repeat(width);
        lines.push(styleText(lineText, style));
    }
    return lines.join("\n");
}
/**
 * Slice a multiline ANSI string by visual row indices.
 */
function sliceMultilineAnsi(text, rowOffset, rowCount) {
    if (!text || rowCount <= 0)
        return "";
    const lines = text.split("\n");
    return lines.slice(rowOffset, rowOffset + rowCount).join("\n");
}

const GitHubLight = {
    "code.hljs": { padding: "3px 5px" },
    hljs: { color: "#24292e", background: "#ffffff" },
    "hljs-doctag": { color: "#d73a49" },
    "hljs-keyword": { color: "#d73a49" },
    "hljs-template-tag": { color: "#d73a49" },
    "hljs-template-variable": { color: "#d73a49" },
    "hljs-type": { color: "#d73a49" },
    "hljs-variable.language_": { color: "#d73a49" },
    "hljs-title": { color: "#6f42c1" },
    "hljs-title.class_": { color: "#6f42c1" },
    "hljs-title.class_.inherited__": { color: "#6f42c1" },
    "hljs-title.function_": { color: "#6f42c1" },
    "hljs-attr": { color: "#005cc5" },
    "hljs-attribute": { color: "#005cc5" },
    "hljs-literal": { color: "#005cc5" },
    "hljs-meta": { color: "#005cc5" },
    "hljs-number": { color: "#005cc5" },
    "hljs-operator": { color: "#005cc5" },
    "hljs-variable": { color: "#005cc5" },
    "hljs-selector-attr": { color: "#005cc5" },
    "hljs-selector-class": { color: "#005cc5" },
    "hljs-selector-id": { color: "#005cc5" },
    "hljs-regexp": { color: "#032f62" },
    "hljs-string": { color: "#032f62" },
    "hljs-built_in": { color: "#e36209" },
    "hljs-symbol": { color: "#e36209" },
    "hljs-comment": { color: "#6a737d" },
    "hljs-code": { color: "#6a737d" },
    "hljs-formula": { color: "#6a737d" },
    "hljs-name": { color: "#22863a" },
    "hljs-quote": { color: "#22863a" },
    "hljs-selector-tag": { color: "#22863a" },
    "hljs-selector-pseudo": { color: "#22863a" },
    "hljs-subst": { color: "#24292e" },
    "hljs-section": { color: "#005cc5", "font-weight": "bold" },
    "hljs-bullet": { color: "#735c0f" },
    "hljs-emphasis": { color: "#24292e", "font-style": "italic" },
    "hljs-strong": { color: "#24292e", "font-weight": "bold" },
    "hljs-addition": { color: "#22863a", "background-color": "#f0fff4" },
    "hljs-deletion": { color: "#b31d28", "background-color": "#ffeef0" },
    "hljs-char.escape_": {},
    "hljs-link": {},
    "hljs-params": {},
    "hljs-property": {},
    "hljs-punctuation": {},
    "hljs-tag": {},
};
const GitHubDark = {
    "code.hljs": { padding: "3px 5px" },
    hljs: { color: "#c9d1d9", background: "#0d1117" },
    "hljs-doctag": { color: "#ff7b72" },
    "hljs-keyword": { color: "#ff7b72" },
    "hljs-template-tag": { color: "#ff7b72" },
    "hljs-template-variable": { color: "#ff7b72" },
    "hljs-type": { color: "#ff7b72" },
    "hljs-variable.language_": { color: "#ff7b72" },
    "hljs-title": { color: "#d2a8ff" },
    "hljs-title.class_": { color: "#d2a8ff" },
    "hljs-title.class_.inherited__": { color: "#d2a8ff" },
    "hljs-title.function_": { color: "#d2a8ff" },
    "hljs-attr": { color: "#79c0ff" },
    "hljs-attribute": { color: "#79c0ff" },
    "hljs-literal": { color: "#79c0ff" },
    "hljs-meta": { color: "#79c0ff" },
    "hljs-number": { color: "#79c0ff" },
    "hljs-operator": { color: "#79c0ff" },
    "hljs-variable": { color: "#79c0ff" },
    "hljs-selector-attr": { color: "#79c0ff" },
    "hljs-selector-class": { color: "#79c0ff" },
    "hljs-selector-id": { color: "#79c0ff" },
    "hljs-regexp": { color: "#a5d6ff" },
    "hljs-string": { color: "#a5d6ff" },
    "hljs-built_in": { color: "#ffa657" },
    "hljs-symbol": { color: "#ffa657" },
    "hljs-comment": { color: "#8b949e" },
    "hljs-code": { color: "#8b949e" },
    "hljs-formula": { color: "#8b949e" },
    "hljs-name": { color: "#7ee787" },
    "hljs-quote": { color: "#7ee787" },
    "hljs-selector-tag": { color: "#7ee787" },
    "hljs-selector-pseudo": { color: "#7ee787" },
    "hljs-subst": { color: "#c9d1d9" },
    "hljs-section": { color: "#1f6feb", "font-weight": "bold" },
    "hljs-bullet": { color: "#f2cc60" },
    "hljs-emphasis": { color: "#c9d1d9", "font-style": "italic" },
    "hljs-strong": { color: "#c9d1d9", "font-weight": "bold" },
    "hljs-addition": { color: "#aff5b4", "background-color": "#033a16" },
    "hljs-deletion": { color: "#ffdcd7", "background-color": "#67060c" },
    "hljs-char.escape_": {},
    "hljs-link": {},
    "hljs-params": {},
    "hljs-property": {},
    "hljs-punctuation": {},
    "hljs-tag": {},
};

const defaultTheme = {
    addContent: { light: "#e6ffec", dark: "#14261f" },
    delContent: { light: "#ffebe9", dark: "#311b1f" },
    plainContent: { light: "#ffffff", dark: "#0d1117" },
    expandContent: { light: "#fafafa", dark: "#161b22" },
    emptyContent: { light: "#fafafa", dark: "#161b22" },
    addLineNumber: { light: "#ccffd8", dark: "#1f4429" },
    delLineNumber: { light: "#ffd7d5", dark: "#552527" },
    plainLineNumber: { light: "#fafafa", dark: "#161b22" },
    expandLineNumber: { light: "#fafafa", dark: "#161b22" },
    hunkLineNumber: { light: "#c7ecff", dark: "#204274" },
    plainLineNumberColor: { light: "#555555", dark: "#a0aaab" },
    expandLineNumberColor: { light: "#555555", dark: "#a0aaab" },
    hunkContentColor: { light: "#777777", dark: "#9298a0" },
    addContentHighlight: { light: "#abf2bc", dark: "#1f572d" },
    delContentHighlight: { light: "#ffb3ad", dark: "#80312f" },
    hunkContent: { light: "#ddf4ff", dark: "#131d2e" },
    border: { light: "#dedede", dark: "#3d444d" },
    noBGAddContent: { light: "#b7f0c4", dark: "#1a3a24" },
    noBGDelContent: { light: "#f5c2bf", dark: "#3f2022" },
    noBGAddLineNumber: { light: "#8ae09d", dark: "#27522f" },
    noBGDelLineNumber: { light: "#f0a9a5", dark: "#6d2d2f" },
    noBGAddContentHighlight: { light: "#5fce78", dark: "#286e38" },
    noBGDelContentHighlight: { light: "#f07e78", dark: "#a33e3b" },
};
const buildTheme = (overrides) => {
    if (!overrides)
        return defaultTheme;
    const result = { ...defaultTheme };
    for (const key of Object.keys(overrides)) {
        const val = overrides[key];
        if (val) {
            result[key] = { ...defaultTheme[key], ...val };
        }
    }
    return result;
};
const isThemeEqual = (a, b) => {
    if (a === b)
        return true;
    const keys = Object.keys(a);
    for (const key of keys) {
        if (a[key].light !== b[key].light || a[key].dark !== b[key].dark)
            return false;
    }
    return true;
};

const getTabWidthValue = (tabWidth) => {
    return tabWidth === "small" ? 1 : tabWidth === "medium" ? 2 : 4;
};
const processCharsForAnsi = (str, enableTabSpace, tabWidth, baseStyle, highlightStyle) => {
    const result = [];
    const style = highlightStyle || baseStyle;
    const tabWidthValue = getTabWidthValue(tabWidth);
    for (const char of str) {
        if (enableTabSpace && char === " ") {
            // Show space as dimmed dot
            result.push({ char: "·", style: { ...style, dim: true } });
        }
        else if (char === "\t") {
            if (enableTabSpace) {
                // Show tab as arrow followed by spaces
                result.push({ char: "→", style: { ...style, dim: true } });
                for (let i = 1; i < tabWidthValue; i++) {
                    result.push({ char: " ", style });
                }
            }
            else {
                // Just show spaces for tab
                for (let i = 0; i < tabWidthValue; i++) {
                    result.push({ char: " ", style });
                }
            }
        }
        else {
            result.push({ char, style });
        }
    }
    return result;
};
// for shiki highlighter
const getStyleObjectFromString = memoFunc((str) => {
    const re = { light: {}, dark: {} };
    if (!str)
        return re;
    str.split(";").forEach((el) => {
        const [property, value] = el.split(":");
        if (!property)
            return;
        if (property.trim()?.endsWith("light")) {
            re["light"]["color"] = value.trim();
            return;
        }
        if (property.trim()?.endsWith("dark")) {
            re["dark"]["color"] = value.trim();
            return;
        }
    });
    return re;
});
// for lowlight highlighter
const getStyleFromClassName = memoFunc((className) => {
    const re = { light: {}, dark: {} };
    if (!className)
        return re;
    className.split(" ").forEach((name) => {
        const dark = GitHubDark[name] || {};
        const light = GitHubLight[name] || {};
        Object.assign(re.dark, dark);
        Object.assign(re.light, light);
    });
    return re;
});
/**
 * DiffString component using ANSI escape codes for proper character-level wrapping.
 *
 * The key insight: When using multiple <Text> components inside a <Box flexWrap="wrap">,
 * Yoga layout treats each <Text> as a flex item and tries to keep them together as "words".
 *
 * Solution: Build a single string with ANSI escape codes. Ink's wrap-ansi (with hard: true)
 * will break at character boundaries, giving us the desired character-level wrapping.
 */
const DiffString = React.memo(({ bg, width, theme, rawLine, diffLine, operator, noBG, themeColors, scrollSlice, }) => {
    const changes = diffLine?.changes;
    const { useDiffContext } = useDiffViewContext();
    const { enableTabSpace, tabWidth } = useDiffContext.useShallowStableSelector((s) => ({
        enableTabSpace: s.tabSpace,
        tabWidth: s.tabWidth,
    }));
    const ansiContent = React.useMemo(() => {
        const chars = [];
        const baseStyle = { backgroundColor: bg };
        if (changes?.hasLineChange) {
            const range = changes.range;
            const str1 = rawLine.slice(0, range.location);
            const str2 = rawLine.slice(range.location, range.location + range.length);
            const str3 = rawLine.slice(range.location + range.length);
            const addHighlight = noBG ? themeColors.noBGAddContentHighlight : themeColors.addContentHighlight;
            const delHighlight = noBG ? themeColors.noBGDelContentHighlight : themeColors.delContentHighlight;
            const highlightBG = operator === "add"
                ? theme === "light"
                    ? addHighlight.light
                    : addHighlight.dark
                : theme === "light"
                    ? delHighlight.light
                    : delHighlight.dark;
            const highlightStyle = { backgroundColor: highlightBG };
            chars.push(...processCharsForAnsi(str1, enableTabSpace, tabWidth, baseStyle));
            chars.push(...processCharsForAnsi(str2, enableTabSpace, tabWidth, baseStyle, highlightStyle));
            chars.push(...processCharsForAnsi(str3, enableTabSpace, tabWidth, baseStyle));
            // Add newline symbol indicator if needed
            if (changes.newLineSymbol === NewLineSymbol.NEWLINE) {
                const noNewlineText = "\\ No newline at end of file";
                for (const char of noNewlineText) {
                    chars.push({ char, style: baseStyle });
                }
            }
        }
        else {
            // No line changes, just process the whole line
            chars.push(...processCharsForAnsi(rawLine, enableTabSpace, tabWidth, baseStyle));
        }
        // Use width - 2 because the operator column takes 1 character and end padding takes 1 character
        const full = buildAnsiStringWithLineBreaks(chars, width - 2);
        if (!scrollSlice)
            return full;
        return sliceMultilineAnsi(full, scrollSlice.rowOffset, scrollSlice.rowCount);
    }, [bg, width, theme, rawLine, changes, operator, enableTabSpace, tabWidth, noBG, themeColors, scrollSlice]);
    return (jsx(Box, { width: width - 2, backgroundColor: bg, children: jsx(Text, { wrap: "truncate", children: ansiContent }) }));
});
DiffString.displayName = "DiffString";
const processSyntaxCharsForAnsi = (str, enableTabSpace, tabWidth, baseStyle, syntaxColor, highlightBg) => {
    const result = [];
    const tabWidthValue = getTabWidthValue(tabWidth);
    for (const char of str) {
        const style = {
            ...baseStyle,
            color: syntaxColor || baseStyle.color,
            backgroundColor: highlightBg || baseStyle.backgroundColor,
        };
        if (enableTabSpace && char === " ") {
            result.push({ char: "·", style: { ...style, dim: true } });
        }
        else if (char === "\t") {
            if (enableTabSpace) {
                result.push({ char: "→", style: { ...style, dim: true } });
                for (let i = 1; i < tabWidthValue; i++) {
                    result.push({ char: " ", style });
                }
            }
            else {
                for (let i = 0; i < tabWidthValue; i++) {
                    result.push({ char: " ", style });
                }
            }
        }
        else {
            result.push({ char, style });
        }
    }
    return result;
};
/**
 * DiffSyntax component using ANSI escape codes for proper character-level wrapping
 * with syntax highlighting support.
 */
const DiffSyntax = React.memo(({ bg, width, theme, height, rawLine, diffLine, operator, syntaxLine, noBG, themeColors, scrollSlice, }) => {
    const { useDiffContext } = useDiffViewContext();
    const { enableTabSpace, tabWidth } = useDiffContext.useShallowStableSelector((s) => ({
        enableTabSpace: s.tabSpace,
        tabWidth: s.tabWidth,
    }));
    const ansiContent = React.useMemo(() => {
        if (!syntaxLine) {
            return null;
        }
        const changes = diffLine?.changes;
        const chars = [];
        const baseStyle = { backgroundColor: bg };
        const addHighlight = noBG ? themeColors.noBGAddContentHighlight : themeColors.addContentHighlight;
        const delHighlight = noBG ? themeColors.noBGDelContentHighlight : themeColors.delContentHighlight;
        const highlightBG = operator === "add"
            ? theme === "light"
                ? addHighlight.light
                : addHighlight.dark
            : theme === "light"
                ? delHighlight.light
                : delHighlight.dark;
        const range = changes?.hasLineChange ? changes.range : null;
        for (const { node, wrapper } of syntaxLine.nodeList || []) {
            // Get syntax color from lowlight or shiki
            const lowlightStyles = getStyleFromClassName(wrapper?.properties?.className?.join(" ") || "");
            const lowlightStyle = theme === "dark" ? lowlightStyles.dark : lowlightStyles.light;
            const shikiStyles = getStyleObjectFromString(wrapper?.properties?.style || "");
            const shikiStyle = theme === "dark" ? shikiStyles.dark : shikiStyles.light;
            // Determine the syntax color (shiki style takes precedence)
            const syntaxColor = shikiStyle?.color || lowlightStyle?.color;
            if (!range) {
                // No line changes, just apply syntax highlighting
                chars.push(...processSyntaxCharsForAnsi(node.value, enableTabSpace, tabWidth, { ...baseStyle, color: syntaxColor }));
            }
            else {
                // Has line changes, need to handle highlighting ranges
                if (node.endIndex < range.location || range.location + range.length < node.startIndex) {
                    // Node is completely outside the change range
                    chars.push(...processSyntaxCharsForAnsi(node.value, enableTabSpace, tabWidth, { ...baseStyle, color: syntaxColor }));
                }
                else {
                    // Node overlaps with the change range
                    const index1 = range.location - node.startIndex;
                    const index2 = index1 < 0 ? 0 : index1;
                    const str1 = node.value.slice(0, index2);
                    const str2 = node.value.slice(index2, index1 + range.length);
                    const str3 = node.value.slice(index1 + range.length);
                    // Before highlight
                    if (str1) {
                        chars.push(...processSyntaxCharsForAnsi(str1, enableTabSpace, tabWidth, { ...baseStyle, color: syntaxColor }));
                    }
                    // Highlighted part
                    if (str2) {
                        chars.push(...processSyntaxCharsForAnsi(str2, enableTabSpace, tabWidth, { ...baseStyle, color: syntaxColor }, syntaxColor, highlightBG));
                    }
                    // After highlight
                    if (str3) {
                        chars.push(...processSyntaxCharsForAnsi(str3, enableTabSpace, tabWidth, { ...baseStyle, color: syntaxColor }));
                    }
                }
            }
        }
        // Add newline symbol indicator if needed
        if (changes?.hasLineChange && changes.newLineSymbol === NewLineSymbol.NEWLINE) {
            const noNewlineText = "\\ No newline at end of file";
            for (const char of noNewlineText) {
                chars.push({ char, style: baseStyle });
            }
        }
        // Use width - 2 because the operator column takes 1 character and end padding takes 1 character
        const full = buildAnsiStringWithLineBreaks(chars, width - 2);
        if (!scrollSlice)
            return full;
        return sliceMultilineAnsi(full, scrollSlice.rowOffset, scrollSlice.rowCount);
    }, [bg, width, theme, diffLine, operator, syntaxLine, enableTabSpace, tabWidth, noBG, themeColors, scrollSlice]);
    // Fallback to DiffString if no syntax line
    if (!syntaxLine) {
        return (jsx(DiffString, { bg: bg, width: width, theme: theme, height: height, rawLine: rawLine, diffLine: diffLine, operator: operator, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice }));
    }
    return (jsx(Box, { width: width - 2, backgroundColor: bg, children: jsx(Text, { wrap: "truncate", children: ansiContent }) }));
});
DiffSyntax.displayName = "DiffSyntax";
/**
 * DiffOperator component - Renders the operator column (+, -, or space)
 * using chalk for proper multi-row support.
 */
const DiffOperator = React.memo(({ operatorChar, height, backgroundColor, rowOffset = 0, }) => {
    const content = React.useMemo(() => {
        const lines = [];
        const style = { backgroundColor };
        for (let row = 0; row < height; row++) {
            const absoluteRow = rowOffset + row;
            const char = absoluteRow === 0 ? operatorChar : " ";
            lines.push(buildStyledBlock(char, 1, 1, style, "left"));
        }
        return lines.join("\n");
    }, [operatorChar, height, backgroundColor, rowOffset]);
    return (jsx(Box, { width: 1, flexShrink: 0, children: jsx(Text, { wrap: "truncate", children: content }) }));
});
DiffOperator.displayName = "DiffOperator";
/**
 * DiffPadding component - Renders a 1-char padding column
 * using chalk for proper multi-row support.
 */
const DiffPadding = React.memo(({ height, backgroundColor }) => {
    const content = React.useMemo(() => {
        const lines = [];
        const style = { backgroundColor };
        for (let row = 0; row < height; row++) {
            lines.push(buildStyledBlock(" ", 1, 1, style, "left"));
        }
        return lines.join("\n");
    }, [height, backgroundColor]);
    return (jsx(Box, { width: 1, flexShrink: 0, children: jsx(Text, { wrap: "truncate", children: content }) }));
});
DiffPadding.displayName = "DiffPadding";
const DiffContent = React.memo(({ theme, width, height, diffLine, rawLine, plainLine, syntaxLine, enableHighlight, noBG, themeColors, scrollSlice, }) => {
    const { useDiffContext } = useDiffViewContext();
    const hideOperator = useDiffContext.useShallowStableSelector((s) => s.hideOperator);
    const isAdded = diffLine?.type === DiffLineType.Add;
    const isDelete = diffLine?.type === DiffLineType.Delete;
    const isMaxLineLengthToIgnoreSyntax = syntaxLine && syntaxLine?.nodeList?.length > 150;
    const bg = React.useMemo(() => {
        const addColors = noBG ? themeColors.noBGAddContent : themeColors.addContent;
        const delColors = noBG ? themeColors.noBGDelContent : themeColors.delContent;
        if (isAdded)
            return theme === "light" ? addColors.light : addColors.dark;
        if (isDelete)
            return theme === "light" ? delColors.light : delColors.dark;
        if (noBG)
            return undefined;
        return theme === "light"
            ? diffLine
                ? themeColors.plainContent.light
                : themeColors.expandContent.light
            : diffLine
                ? themeColors.plainContent.dark
                : themeColors.expandContent.dark;
    }, [theme, diffLine, isAdded, isDelete, noBG, themeColors]);
    const operatorChar = isAdded ? "+" : isDelete ? "-" : " ";
    return (jsxs(Box, { height: height, width: width, children: [hideOperator ? (jsx(DiffPadding, { height: height, backgroundColor: bg })) : (jsx(DiffOperator, { operatorChar: operatorChar, height: height, backgroundColor: bg, rowOffset: scrollSlice?.rowOffset })), enableHighlight && syntaxLine && !isMaxLineLengthToIgnoreSyntax ? (jsx(DiffSyntax, { bg: bg, theme: theme, width: width, height: height, operator: isAdded ? "add" : isDelete ? "del" : undefined, rawLine: rawLine, diffLine: diffLine, syntaxLine: syntaxLine, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice })) : (jsx(DiffString, { bg: bg, theme: theme, width: width, height: height, operator: isAdded ? "add" : isDelete ? "del" : undefined, rawLine: rawLine, diffLine: diffLine, plainLine: plainLine, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice })), jsx(DiffPadding, { height: height, backgroundColor: bg })] }));
});
DiffContent.displayName = "DiffContent";

/**
 * Renders a single line number area for split view.
 * Format: [ ][lineNum][ ]
 */
const DiffSplitLineNumberArea = React.memo(({ lineNumber, lineNumWidth, height, rowOffset = 0, backgroundColor, color, dim = false }) => {
    // Total width: leftPad + num + rightPad = 1 + lineNumWidth + 1
    const totalWidth = lineNumWidth + 2;
    const content = React.useMemo(() => {
        const style = { backgroundColor, color, dim };
        const lines = [];
        for (let row = 0; row < height; row++) {
            const absoluteRow = rowOffset + row;
            // Left padding + line number (right-aligned) + right padding
            const numPart = absoluteRow === 0 && lineNumber !== undefined
                ? lineNumber.toString().padStart(lineNumWidth)
                : " ".repeat(lineNumWidth);
            const lineText = ` ${numPart} `;
            lines.push(buildStyledBlock(lineText, totalWidth, 1, style, "left"));
        }
        return lines.join("\n");
    }, [lineNumber, lineNumWidth, height, rowOffset, backgroundColor, color, dim, totalWidth]);
    return (jsx(Box, { width: totalWidth, flexShrink: 0, children: jsx(Text, { wrap: "truncate", children: content }) }));
});
DiffSplitLineNumberArea.displayName = "DiffSplitLineNumberArea";
/**
 * Renders the complete line number area for unified view (old + new line numbers).
 * Format: [oldNum] [newNum]
 */
const DiffUnifiedLineNumberArea = React.memo(({ oldLineNumber, newLineNumber, lineNumWidth, height, rowOffset = 0, backgroundColor, color, dim = false }) => {
    // Total width: oldNum + separator + newNum + separator = lineNumWidth + 1 + lineNumWidth + 1
    const totalWidth = lineNumWidth * 2 + 3;
    const content = React.useMemo(() => {
        const style = { backgroundColor, color, dim };
        const lines = [];
        for (let row = 0; row < height; row++) {
            const absoluteRow = rowOffset + row;
            // Old number (right-aligned) + separator + new number (right-aligned) + separator
            const oldPart = absoluteRow === 0 && oldLineNumber !== undefined
                ? oldLineNumber.toString().padStart(lineNumWidth)
                : " ".repeat(lineNumWidth);
            const newPart = absoluteRow === 0 && newLineNumber !== undefined
                ? newLineNumber.toString().padStart(lineNumWidth)
                : " ".repeat(lineNumWidth);
            const lineText = ` ${oldPart} ${newPart} `;
            lines.push(buildStyledBlock(lineText, totalWidth, 1, style, "left"));
        }
        return lines.join("\n");
    }, [oldLineNumber, newLineNumber, lineNumWidth, height, rowOffset, backgroundColor, color, dim, totalWidth]);
    return (jsx(Box, { width: totalWidth, flexShrink: 0, children: jsx(Text, { wrap: "truncate", children: content }) }));
});
DiffUnifiedLineNumberArea.displayName = "DiffUnifiedLineNumberArea";
/**
 * Renders an empty area (for split view when one side has no content).
 */
const DiffEmptyArea = React.memo(({ width, height, backgroundColor }) => {
    const content = React.useMemo(() => {
        const style = { backgroundColor };
        const lines = [];
        for (let row = 0; row < height; row++) {
            lines.push(buildStyledBlock("", width, 1, style, "left"));
        }
        return lines.join("\n");
    }, [width, height, backgroundColor]);
    return (jsx(Box, { width: width, children: jsx(Text, { wrap: "truncate", children: content }) }));
});
DiffEmptyArea.displayName = "DiffEmptyArea";

configureEnv({ allowNonBrowserUpdates: true });
const createDiffConfigStore = (props, diffFileId) => {
    return createStore(() => {
        const id = ref(diffFileId);
        const setId = (_id) => (id.value = _id);
        const mode = ref(props.diffViewMode);
        const setMode = (_mode) => (mode.value = _mode);
        const width = ref(props.width);
        const setWidth = (_width) => (width.value = _width);
        const tabSpace = ref(props.diffViewTabSpace);
        const setTabSpace = (_tabSpace) => (tabSpace.value = _tabSpace);
        const tabWidth = ref(props.diffViewTabWidth || "medium");
        const setTabWidth = (_tabWidth) => (tabWidth.value = _tabWidth);
        const wrapper = ref(markRaw({ current: null }));
        const setWrapper = (_wrapper) => (wrapper.value = markRaw({ current: _wrapper || null }));
        const enableHighlight = ref(props.diffViewHighlight);
        const setEnableHighlight = (_enableHighlight) => (enableHighlight.value = _enableHighlight);
        const extendData = ref({
            oldFile: { ...props.extendData?.oldFile },
            newFile: { ...props.extendData?.newFile },
        });
        const setExtendData = (__extendData) => {
            const _extendData = __extendData || {};
            const existOldKeys = Object.keys(extendData.value.oldFile || {});
            const inComingOldKeys = Object.keys(_extendData.oldFile || {});
            for (const key of existOldKeys) {
                if (!inComingOldKeys.includes(key)) {
                    delete extendData.value.oldFile[key];
                }
            }
            for (const key of inComingOldKeys) {
                extendData.value.oldFile[key] = _extendData.oldFile[key];
            }
            const existNewKeys = Object.keys(extendData.value.newFile || {});
            const inComingNewKeys = Object.keys(_extendData.newFile || {});
            for (const key of existNewKeys) {
                if (!inComingNewKeys.includes(key)) {
                    delete extendData.value.newFile[key];
                }
            }
            for (const key of inComingNewKeys) {
                extendData.value.newFile[key] = _extendData.newFile[key];
            }
        };
        const renderExtendLine = ref(props.renderExtendLine);
        const setRenderExtendLine = (_renderExtendLine) => (renderExtendLine.value = _renderExtendLine);
        const hideOperator = ref(props.diffViewHideOperator);
        const setHideOperator = (_hideOperator) => (hideOperator.value = _hideOperator);
        const noBG = ref(props.diffViewNoBG);
        const setNoBG = (_noBG) => (noBG.value = _noBG);
        const themeColors = ref(buildTheme(props.diffViewThemeColors));
        const setThemeColors = (_themeColors) => {
            const next = buildTheme(_themeColors);
            if (!isThemeEqual(themeColors.value, next)) {
                themeColors.value = next;
            }
        };
        return {
            id,
            setId,
            mode,
            setMode,
            wrapper,
            width,
            setWidth,
            setWrapper,
            tabSpace,
            setTabSpace,
            tabWidth,
            setTabWidth,
            enableHighlight,
            setEnableHighlight,
            extendData,
            setExtendData,
            renderExtendLine,
            setRenderExtendLine,
            hideOperator,
            setHideOperator,
            noBG,
            setNoBG,
            themeColors,
            setThemeColors,
        };
        // fix rollup type error
    });
};
const getCurrentLineRow = ({ content, width }) => {
    // Ensure minimum of 1 row for empty lines
    return Math.max(1, Math.ceil(stringWidth(content) / width));
};

const InternalDiffSplitLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, enableHighlight, noBG, themeColors, scrollSlice, }) => {
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const oldSyntaxLine = diffFile.getOldSyntaxLine(oldLine?.lineNumber ?? -1);
    const oldPlainLine = diffFile.getOldPlainLine(oldLine.lineNumber ?? -1);
    const newSyntaxLine = diffFile.getNewSyntaxLine(newLine?.lineNumber ?? -1);
    const newPlainLine = diffFile.getNewPlainLine(newLine.lineNumber ?? -1);
    const hasDiff = !!oldLine?.diff || !!newLine?.diff;
    const hasChange = checkDiffLineIncludeChange(oldLine?.diff) || checkDiffLineIncludeChange(newLine?.diff);
    const oldLineIsDelete = oldLine?.diff?.type === DiffLineType.Delete;
    const newLineIsAdded = newLine?.diff?.type === DiffLineType.Add;
    const hasOldLine = !!oldLine.lineNumber;
    const hasNewLine = !!newLine.lineNumber;
    const emptyBG = noBG ? undefined : theme === "light" ? themeColors.emptyContent.light : themeColors.emptyContent.dark;
    const halfColumns = columns / 2;
    const contentWidth = columns / 2 - lineNumWidth - 2;
    // Calculate row heights - must match the actual wrap width used in DiffContent
    // DiffContent receives contentWidth and wraps at (contentWidth - 2) for the operator column and end padding
    const noNewlineText = "\\ No newline at end of file";
    // Include "No newline" text in row calculation if present
    const oldContent = oldLine?.diff?.changes?.hasLineChange && oldLine?.diff?.changes.newLineSymbol === NewLineSymbol.NEWLINE
        ? (oldLine?.value || "") + noNewlineText
        : oldLine?.value || "";
    const newContent = newLine?.diff?.changes?.hasLineChange && newLine?.diff?.changes.newLineSymbol === NewLineSymbol.NEWLINE
        ? (newLine?.value || "") + noNewlineText
        : newLine?.value || "";
    const oldRow = getCurrentLineRow({ content: oldContent, width: contentWidth - 2 });
    const newRow = getCurrentLineRow({ content: newContent, width: contentWidth - 2 });
    const row = Math.max(oldRow, newRow);
    const visibleHeight = scrollSlice?.rowCount ?? row;
    const delLnColors = noBG ? themeColors.noBGDelLineNumber : themeColors.delLineNumber;
    const addLnColors = noBG ? themeColors.noBGAddLineNumber : themeColors.addLineNumber;
    const oldLineNumberBG = oldLineIsDelete
        ? theme === "light"
            ? delLnColors.light
            : delLnColors.dark
        : noBG
            ? undefined
            : hasDiff
                ? theme === "light"
                    ? themeColors.plainLineNumber.light
                    : themeColors.plainLineNumber.dark
                : theme === "light"
                    ? themeColors.expandLineNumber.light
                    : themeColors.expandLineNumber.dark;
    const newLineNumberBG = newLineIsAdded
        ? theme === "light"
            ? addLnColors.light
            : addLnColors.dark
        : noBG
            ? undefined
            : hasDiff
                ? theme === "light"
                    ? themeColors.plainLineNumber.light
                    : themeColors.plainLineNumber.dark
                : theme === "light"
                    ? themeColors.expandLineNumber.light
                    : themeColors.expandLineNumber.dark;
    const color = hasDiff
        ? theme === "light"
            ? themeColors.plainLineNumberColor.light
            : themeColors.plainLineNumberColor.dark
        : theme === "light"
            ? themeColors.expandLineNumberColor.light
            : themeColors.expandLineNumberColor.dark;
    return (jsxs(Box, { "data-line": lineNumber, "data-state": hasDiff ? "diff" : "plain", height: visibleHeight, width: columns, children: [hasOldLine ? (jsxs(Fragment, { children: [jsx(DiffSplitLineNumberArea, { lineNumber: oldLine.lineNumber, lineNumWidth: lineNumWidth, height: visibleHeight, rowOffset: scrollSlice?.rowOffset, backgroundColor: oldLineNumberBG, color: color, dim: !hasChange }), jsx(DiffContent, { theme: theme, height: visibleHeight, diffFile: diffFile, width: contentWidth, rawLine: oldLine.value || "", diffLine: oldLine.diff, plainLine: oldPlainLine, syntaxLine: oldSyntaxLine, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice })] })) : (jsx(DiffEmptyArea, { width: halfColumns, height: visibleHeight, backgroundColor: emptyBG })), hasNewLine ? (jsxs(Fragment, { children: [jsx(DiffSplitLineNumberArea, { lineNumber: newLine.lineNumber, lineNumWidth: lineNumWidth, height: visibleHeight, rowOffset: scrollSlice?.rowOffset, backgroundColor: newLineNumberBG, color: color, dim: !hasChange }), jsx(DiffContent, { theme: theme, height: visibleHeight, width: contentWidth, diffFile: diffFile, rawLine: newLine.value || "", diffLine: newLine.diff, plainLine: newPlainLine, syntaxLine: newSyntaxLine, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice })] })) : (jsx(DiffEmptyArea, { width: halfColumns, height: visibleHeight, backgroundColor: emptyBG }))] }));
};
const DiffSplitContentLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, enableHighlight, noBG, themeColors, scrollSlice, }) => {
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    if (oldLine?.isHidden && newLine?.isHidden)
        return null;
    return (jsx(InternalDiffSplitLine, { index: index, theme: theme, columns: columns, diffFile: diffFile, lineNumber: lineNumber, lineNumWidth: lineNumWidth, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice }));
};

const InternalDiffSplitExtendLine = ({ index, columns, theme, diffFile, lineNumber, oldLineExtend, newLineExtend, noBG, themeColors, }) => {
    const { useDiffContext } = useDiffViewContext();
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    // 需要显示的时候才进行方法订阅，可以大幅度提高性能
    const renderExtendLine = useDiffContext.useShallowStableSelector((s) => s.renderExtendLine);
    if (!renderExtendLine)
        return null;
    const oldExtendRendered = oldLineExtend?.data &&
        renderExtendLine?.({
            diffFile,
            side: SplitSide.old,
            lineNumber: oldLine.lineNumber ?? -1,
            data: oldLineExtend.data,
            onUpdate: diffFile.notifyAll,
        });
    const newExtendRendered = newLineExtend?.data &&
        renderExtendLine?.({
            diffFile,
            side: SplitSide.new,
            lineNumber: newLine.lineNumber ?? -1,
            data: newLineExtend.data,
            onUpdate: diffFile.notifyAll,
        });
    const bgColor = noBG ? undefined : theme === "light" ? themeColors.emptyContent.light : themeColors.emptyContent.dark;
    return (jsxs(Box, { "data-line": `${lineNumber}-extend`, "data-state": "extend", children: [jsx(Box, { width: columns / 2, flexShrink: 0, flexGrow: 0, backgroundColor: oldExtendRendered ? undefined : bgColor, children: React.isValidElement(oldExtendRendered) ? oldExtendRendered : jsx(Text, { children: oldExtendRendered }) }), jsx(Box, { width: columns / 2, flexShrink: 0, flexGrow: 0, backgroundColor: newExtendRendered ? undefined : bgColor, children: React.isValidElement(newExtendRendered) ? newExtendRendered : jsx(Text, { children: newExtendRendered }) })] }));
};
const DiffSplitExtendLine = ({ index, columns, theme, diffFile, lineNumber, noBG, themeColors, }) => {
    const { useDiffContext } = useDiffViewContext();
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const { oldLineExtend, newLineExtend } = useDiffContext(React.useCallback((s) => ({
        oldLineExtend: s.extendData?.oldFile?.[oldLine?.lineNumber ?? -1],
        newLineExtend: s.extendData?.newFile?.[newLine?.lineNumber ?? -1],
    }), [oldLine?.lineNumber, newLine?.lineNumber]));
    const hasExtend = oldLineExtend?.data || newLineExtend?.data;
    // if the expand action not enabled, the `isHidden` property will never change
    const enableExpand = diffFile.getExpandEnabled();
    const currentIsShow = hasExtend && ((!oldLine?.isHidden && !newLine?.isHidden) || !enableExpand);
    if (!currentIsShow)
        return null;
    return (jsx(InternalDiffSplitExtendLine, { index: index, theme: theme, noBG: noBG, columns: columns, diffFile: diffFile, lineNumber: lineNumber, oldLineExtend: oldLineExtend, newLineExtend: newLineExtend, themeColors: themeColors }));
};

const DiffSplitHunkLineGitHub = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, noBG, themeColors, }) => {
    const currentHunk = diffFile.getSplitHunkLine(index);
    const hunkLineNumberBG = noBG
        ? undefined
        : theme === "light"
            ? themeColors.hunkLineNumber.light
            : themeColors.hunkLineNumber.dark;
    const hunkContentBG = noBG
        ? undefined
        : theme === "light"
            ? themeColors.hunkContent.light
            : themeColors.hunkContent.dark;
    const hunkContentColor = theme === "light" ? themeColors.hunkContentColor.light : themeColors.hunkContentColor.dark;
    const contentWidth = columns - lineNumWidth - 2;
    return (jsxs(Box, { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", height: 1, children: [jsx(Box, { width: lineNumWidth + 2, children: jsx(Text, { backgroundColor: hunkLineNumberBG, children: " ".padEnd(lineNumWidth + 2) }) }), jsx(Box, { width: contentWidth, children: jsx(Text, { backgroundColor: hunkContentBG, color: hunkContentColor, wrap: "truncate", children: (currentHunk.splitInfo?.plainText || currentHunk.text || "").padEnd(contentWidth) }) })] }));
};
const DiffSplitHunkLineGitLab = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, noBG, themeColors, }) => {
    const currentHunk = diffFile.getSplitHunkLine(index);
    const hunkLineNumberBG = noBG
        ? undefined
        : theme === "light"
            ? themeColors.hunkLineNumber.light
            : themeColors.hunkLineNumber.dark;
    const hunkContentBG = noBG
        ? undefined
        : theme === "light"
            ? themeColors.hunkContent.light
            : themeColors.hunkContent.dark;
    const hunkContentColor = theme === "light" ? themeColors.hunkContentColor.light : themeColors.hunkContentColor.dark;
    const contentWidth = columns / 2 - lineNumWidth - 2;
    return (jsxs(Box, { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", height: 1, children: [jsx(Box, { width: lineNumWidth + 2, children: jsx(Text, { backgroundColor: hunkLineNumberBG, children: " ".padEnd(lineNumWidth + 2) }) }), jsx(Box, { width: contentWidth, children: jsx(Text, { backgroundColor: hunkContentBG, color: hunkContentColor, wrap: "truncate", children: (currentHunk.splitInfo?.plainText || currentHunk.text || "").padEnd(contentWidth) }) }), jsx(Box, { width: lineNumWidth + 2, children: jsx(Text, { backgroundColor: hunkLineNumberBG, children: " ".padEnd(lineNumWidth + 2) }) }), jsx(Box, { width: contentWidth, children: jsx(Text, { backgroundColor: hunkContentBG, color: hunkContentColor, wrap: "truncate", children: (currentHunk.splitInfo?.plainText || currentHunk.text || "").padEnd(contentWidth) }) })] }));
};
const InternalDiffSplitHunkLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, noBG, themeColors, }) => {
    const { useDiffContext } = useDiffViewContext();
    const diffViewMode = useDiffContext.useShallowStableSelector((s) => s.mode);
    if (diffViewMode === DiffModeEnum.SplitGitHub ||
        diffViewMode === DiffModeEnum.Split ||
        diffViewMode === DiffModeEnum.Unified) {
        return (jsx(DiffSplitHunkLineGitHub, { index: index, theme: theme, columns: columns, diffFile: diffFile, lineNumber: lineNumber, lineNumWidth: lineNumWidth, noBG: noBG, themeColors: themeColors }));
    }
    else {
        return (jsx(DiffSplitHunkLineGitLab, { index: index, theme: theme, columns: columns, diffFile: diffFile, lineNumber: lineNumber, lineNumWidth: lineNumWidth, noBG: noBG, themeColors: themeColors }));
    }
};
const DiffSplitHunkLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, noBG, themeColors, }) => {
    const currentHunk = diffFile.getSplitHunkLine(index);
    const currentIsShow = currentHunk &&
        currentHunk.splitInfo &&
        currentHunk.splitInfo.startHiddenIndex < currentHunk.splitInfo.endHiddenIndex;
    const currentIsPureHunk = currentHunk && diffFile._getIsPureDiffRender() && !currentHunk.splitInfo;
    if (!currentIsShow && !currentIsPureHunk)
        return null;
    return (jsx(InternalDiffSplitHunkLine, { index: index, theme: theme, columns: columns, diffFile: diffFile, lineNumber: lineNumber, lineNumWidth: lineNumWidth, noBG: noBG, themeColors: themeColors }));
};

/**
 * Unified view line component for deleted lines (old side only)
 */
const DiffUnifiedOldLine = ({ index, theme, height, columns, rawLine, diffLine, plainLine, syntaxLine, lineNumber, lineNumWidth, diffFile, contentWidth, enableHighlight, noBG, themeColors, scrollSlice, }) => {
    const color = theme === "light" ? themeColors.plainLineNumberColor.light : themeColors.plainLineNumberColor.dark;
    const delLnColors = noBG ? themeColors.noBGDelLineNumber : themeColors.delLineNumber;
    const bg = theme === "light" ? delLnColors.light : delLnColors.dark;
    return (jsxs(Box, { "data-line": index, "data-state": "diff", height: height, width: columns, children: [jsx(DiffUnifiedLineNumberArea, { oldLineNumber: lineNumber, newLineNumber: undefined, lineNumWidth: lineNumWidth, height: height, rowOffset: scrollSlice?.rowOffset, backgroundColor: bg, color: color, dim: false }), jsx(DiffContent, { theme: theme, height: height, width: contentWidth, rawLine: rawLine, diffLine: diffLine, diffFile: diffFile, plainLine: plainLine, syntaxLine: syntaxLine, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice })] }));
};
/**
 * Unified view line component for added lines (new side only)
 */
const DiffUnifiedNewLine = ({ index, theme, height, columns, rawLine, diffLine, plainLine, syntaxLine, lineNumber, lineNumWidth, diffFile, contentWidth, enableHighlight, noBG, themeColors, scrollSlice, }) => {
    const color = theme === "light" ? themeColors.plainLineNumberColor.light : themeColors.plainLineNumberColor.dark;
    const addLnColors = noBG ? themeColors.noBGAddLineNumber : themeColors.addLineNumber;
    const bg = theme === "light" ? addLnColors.light : addLnColors.dark;
    return (jsxs(Box, { "data-line": index, "data-state": "diff", height: height, width: columns, children: [jsx(DiffUnifiedLineNumberArea, { oldLineNumber: undefined, newLineNumber: lineNumber, lineNumWidth: lineNumWidth, height: height, rowOffset: scrollSlice?.rowOffset, backgroundColor: bg, color: color, dim: false }), jsx(DiffContent, { theme: theme, height: height, width: contentWidth, diffFile: diffFile, rawLine: rawLine, diffLine: diffLine, plainLine: plainLine, syntaxLine: syntaxLine, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice })] }));
};
const InternalDiffUnifiedLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, enableHighlight, noBG, themeColors, scrollSlice, }) => {
    const unifiedLine = diffFile.getUnifiedLine(index);
    const hasDiff = unifiedLine.diff;
    const hasChange = checkDiffLineIncludeChange(unifiedLine.diff);
    const rawLine = unifiedLine.value || "";
    const diffLine = unifiedLine.diff;
    const newLineNumber = unifiedLine.newLineNumber;
    const oldLinenumber = unifiedLine.oldLineNumber;
    const syntaxLine = newLineNumber
        ? diffFile.getNewSyntaxLine(newLineNumber)
        : oldLinenumber
            ? diffFile.getOldSyntaxLine(oldLinenumber)
            : undefined;
    const plainLine = newLineNumber
        ? diffFile.getNewPlainLine(newLineNumber)
        : oldLinenumber
            ? diffFile.getOldPlainLine(oldLinenumber)
            : undefined;
    const contentWidth = columns - (lineNumWidth + 1) * 2 - 1;
    // Include "No newline" text in row calculation if present
    const noNewlineText = "\\ No newline at end of file";
    const contentWithSymbol = diffLine?.changes?.hasLineChange && diffLine.changes.newLineSymbol === NewLineSymbol.NEWLINE
        ? rawLine + noNewlineText
        : rawLine;
    // Use contentWidth - 2 to match the actual wrap width in DiffContent (operator column takes 1 char, end padding takes 1 char)
    const row = getCurrentLineRow({ content: contentWithSymbol, width: contentWidth - 2 });
    const visibleHeight = scrollSlice?.rowCount ?? row;
    const color = hasDiff
        ? theme === "light"
            ? themeColors.plainLineNumberColor.light
            : themeColors.plainLineNumberColor.dark
        : theme === "light"
            ? themeColors.expandLineNumberColor.light
            : themeColors.expandLineNumberColor.dark;
    const bg = noBG
        ? undefined
        : hasDiff
            ? theme === "light"
                ? themeColors.plainLineNumber.light
                : themeColors.plainLineNumber.dark
            : theme === "light"
                ? themeColors.expandLineNumber.light
                : themeColors.expandLineNumber.dark;
    // Handle changed lines (additions or deletions)
    if (hasChange) {
        if (unifiedLine.oldLineNumber) {
            return (jsx(DiffUnifiedOldLine, { theme: theme, height: visibleHeight, columns: columns, rawLine: rawLine, diffFile: diffFile, index: lineNumber, diffLine: diffLine, plainLine: plainLine, syntaxLine: syntaxLine, lineNumWidth: lineNumWidth, contentWidth: contentWidth, enableHighlight: enableHighlight, lineNumber: unifiedLine.oldLineNumber, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice }));
        }
        else {
            return (jsx(DiffUnifiedNewLine, { theme: theme, height: visibleHeight, columns: columns, rawLine: rawLine, index: lineNumber, diffLine: diffLine, diffFile: diffFile, plainLine: plainLine, syntaxLine: syntaxLine, lineNumWidth: lineNumWidth, contentWidth: contentWidth, enableHighlight: enableHighlight, lineNumber: unifiedLine.newLineNumber, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice }));
        }
    }
    // Handle unchanged lines (context lines)
    return (jsxs(Box, { "data-line": lineNumber, "data-state": unifiedLine.diff ? "diff" : "plain", height: visibleHeight, width: columns, children: [jsx(DiffUnifiedLineNumberArea, { oldLineNumber: unifiedLine.oldLineNumber, newLineNumber: unifiedLine.newLineNumber, lineNumWidth: lineNumWidth, height: visibleHeight, rowOffset: scrollSlice?.rowOffset, backgroundColor: bg, color: color, dim: true }), jsx(DiffContent, { theme: theme, height: visibleHeight, rawLine: rawLine, diffFile: diffFile, diffLine: diffLine, width: contentWidth, plainLine: plainLine, syntaxLine: syntaxLine, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice })] }));
};
const DiffUnifiedContentLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, enableHighlight, noBG, themeColors, scrollSlice, }) => {
    const unifiedLine = diffFile.getUnifiedLine(index);
    if (unifiedLine?.isHidden)
        return null;
    return (jsx(InternalDiffUnifiedLine, { index: index, theme: theme, columns: columns, diffFile: diffFile, lineNumber: lineNumber, lineNumWidth: lineNumWidth, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice }));
};

const InternalDiffUnifiedExtendLine = ({ index, columns, diffFile, lineNumber, oldLineExtend, newLineExtend, }) => {
    const { useDiffContext } = useDiffViewContext();
    const renderExtendLine = useDiffContext.useShallowStableSelector((s) => s.renderExtendLine);
    const unifiedItem = diffFile.getUnifiedLine(index);
    if (!renderExtendLine)
        return null;
    const oldExtendRendered = oldLineExtend?.data !== null &&
        oldLineExtend?.data !== undefined &&
        renderExtendLine?.({
            diffFile,
            side: SplitSide.old,
            lineNumber: unifiedItem.oldLineNumber ?? -1,
            data: oldLineExtend.data,
            onUpdate: diffFile.notifyAll,
        });
    const newExtendRendered = newLineExtend?.data !== null &&
        newLineExtend?.data !== undefined &&
        renderExtendLine?.({
            diffFile,
            side: SplitSide.new,
            lineNumber: unifiedItem.newLineNumber ?? -1,
            data: newLineExtend.data,
            onUpdate: diffFile.notifyAll,
        });
    return (jsxs(Box, { "data-line": `${lineNumber}-extend`, "data-state": "extend", width: columns, children: [oldExtendRendered && (jsx(Box, { width: columns, flexShrink: 0, flexGrow: 0, children: !React.isValidElement(oldExtendRendered) ? jsx(Text, { children: oldExtendRendered }) : oldExtendRendered })), newExtendRendered && (jsx(Box, { width: columns, flexShrink: 0, flexGrow: 0, children: !React.isValidElement(newExtendRendered) ? jsx(Text, { children: newExtendRendered }) : newExtendRendered }))] }));
};
const DiffUnifiedExtendLine = ({ index, theme, columns, diffFile, lineNumber, }) => {
    const { useDiffContext } = useDiffViewContext();
    const unifiedItem = diffFile.getUnifiedLine(index);
    const { oldLineExtend, newLineExtend } = useDiffContext(useCallback((s) => ({
        oldLineExtend: s.extendData?.oldFile?.[unifiedItem?.oldLineNumber ?? -1],
        newLineExtend: s.extendData?.newFile?.[unifiedItem?.newLineNumber ?? -1],
    }), [unifiedItem.oldLineNumber, unifiedItem.newLineNumber]));
    const hasExtend = oldLineExtend?.data || newLineExtend?.data;
    if (!hasExtend || !unifiedItem || unifiedItem.isHidden)
        return null;
    return (jsx(InternalDiffUnifiedExtendLine, { index: index, theme: theme, columns: columns, diffFile: diffFile, lineNumber: lineNumber, oldLineExtend: oldLineExtend, newLineExtend: newLineExtend }));
};

const InternalDiffUnifiedHunkLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, noBG, themeColors, }) => {
    const currentHunk = diffFile.getUnifiedHunkLine(index);
    const hunkLineNumberBG = noBG
        ? undefined
        : theme === "light"
            ? themeColors.hunkLineNumber.light
            : themeColors.hunkLineNumber.dark;
    const hunkContentBG = noBG
        ? undefined
        : theme === "light"
            ? themeColors.hunkContent.light
            : themeColors.hunkContent.dark;
    const hunkContentColor = theme === "light" ? themeColors.hunkContentColor.light : themeColors.hunkContentColor.dark;
    const contentWidth = columns - lineNumWidth * 2 - 3;
    return (jsxs(Box, { "data-line": `${lineNumber}-hunk`, "data-state": "hunk", height: 1, children: [jsx(Box, { width: lineNumWidth * 2 + 3, flexShrink: 0, children: jsx(Text, { backgroundColor: hunkLineNumberBG, children: " ".padEnd(lineNumWidth * 2 + 3) }) }), jsx(Box, { width: contentWidth, flexShrink: 0, children: jsx(Text, { backgroundColor: hunkContentBG, color: hunkContentColor, wrap: "truncate", children: (currentHunk.unifiedInfo?.plainText || currentHunk.text || "").padEnd(contentWidth) }) })] }));
};
const DiffUnifiedHunkLine = ({ index, theme, columns, diffFile, lineNumber, lineNumWidth, noBG, themeColors, }) => {
    const currentHunk = diffFile.getUnifiedHunkLine(index);
    const currentIsShow = currentHunk &&
        currentHunk.unifiedInfo &&
        currentHunk.unifiedInfo.startHiddenIndex < currentHunk.unifiedInfo.endHiddenIndex;
    const currentIsPureHunk = currentHunk && diffFile._getIsPureDiffRender() && !currentHunk.unifiedInfo;
    if (!currentIsShow && !currentIsPureHunk)
        return null;
    return (jsx(InternalDiffUnifiedHunkLine, { index: index, theme: theme, columns: columns, diffFile: diffFile, lineNumber: lineNumber, lineNumWidth: lineNumWidth, noBG: noBG, themeColors: themeColors }));
};

const DiffDisplayEntry = memo(({ entry, diffFile, mode, theme, columns, lineNumWidth, enableHighlight, noBG, themeColors, scrollSlice, entryRowCount, }) => {
    const { kind, diffIndex, displayLineNumber } = entry;
    const isSplit = !!(mode & DiffModeEnum.Split);
    if (kind === "hunk") {
        if (isSplit) {
            return (jsx(DiffSplitHunkLine, { theme: theme, columns: columns, index: diffIndex, diffFile: diffFile, lineNumber: displayLineNumber, lineNumWidth: lineNumWidth, noBG: noBG, themeColors: themeColors }));
        }
        return (jsx(DiffUnifiedHunkLine, { index: diffIndex, theme: theme, lineNumWidth: lineNumWidth, columns: columns, diffFile: diffFile, lineNumber: displayLineNumber, noBG: noBG, themeColors: themeColors }));
    }
    if (kind === "content") {
        if (isSplit) {
            return (jsx(DiffSplitContentLine, { theme: theme, lineNumWidth: lineNumWidth, columns: columns, index: diffIndex, diffFile: diffFile, lineNumber: displayLineNumber, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice }));
        }
        return (jsx(DiffUnifiedContentLine, { index: diffIndex, theme: theme, columns: columns, diffFile: diffFile, lineNumWidth: lineNumWidth, lineNumber: displayLineNumber, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollSlice }));
    }
    const extendHeight = scrollSlice?.rowCount ?? entryRowCount;
    if (isSplit) {
        return (jsx(Box, { height: extendHeight, overflow: scrollSlice ? "hidden" : undefined, children: jsx(DiffSplitExtendLine, { theme: theme, columns: columns, index: diffIndex, diffFile: diffFile, lineNumber: displayLineNumber, noBG: noBG, themeColors: themeColors }) }));
    }
    return (jsx(Box, { height: extendHeight, overflow: scrollSlice ? "hidden" : undefined, children: jsx(DiffUnifiedExtendLine, { index: diffIndex, theme: theme, columns: columns, diffFile: diffFile, lineNumber: displayLineNumber }) }));
});
DiffDisplayEntry.displayName = "DiffDisplayEntry";

const EMPTY_DIFF_VIEW_SCROLL_LAYOUT = {
    rows: [],
    lines: [],
    totalRows: 0,
    totalLines: 0,
};
const noNewlineText = "\\ No newline at end of file";
function pushScrollEntry(rows, lines, kind, diffIndex, rowCount) {
    if (rowCount <= 0)
        return;
    const startRow = rows.length;
    for (let i = 0; i < rowCount; i++) {
        rows.push("");
    }
    lines.push({
        lineNumber: lines.length + 1,
        startRow,
        endRow: rows.length,
        kind,
        diffIndex,
    });
}
function shouldShowUnifiedHunk(diffFile, index) {
    const currentHunk = diffFile.getUnifiedHunkLine(index);
    const currentIsShow = !!currentHunk &&
        !!currentHunk.unifiedInfo &&
        currentHunk.unifiedInfo.startHiddenIndex < currentHunk.unifiedInfo.endHiddenIndex;
    const currentIsPureHunk = !!currentHunk && diffFile._getIsPureDiffRender() && !currentHunk.unifiedInfo;
    return currentIsShow || currentIsPureHunk;
}
function shouldShowSplitHunk(diffFile, index) {
    const currentHunk = diffFile.getSplitHunkLine(index);
    const currentIsShow = !!currentHunk &&
        !!currentHunk.splitInfo &&
        currentHunk.splitInfo.startHiddenIndex < currentHunk.splitInfo.endHiddenIndex;
    const currentIsPureHunk = !!currentHunk && diffFile._getIsPureDiffRender() && !currentHunk.splitInfo;
    return currentIsShow || currentIsPureHunk;
}
function shouldShowUnifiedExtend(diffFile, index, extendData, hasRenderExtendLine) {
    if (!hasRenderExtendLine)
        return false;
    const unifiedItem = diffFile.getUnifiedLine(index);
    if (!unifiedItem || unifiedItem.isHidden)
        return false;
    const oldLineExtend = extendData?.oldFile?.[unifiedItem.oldLineNumber ?? -1];
    const newLineExtend = extendData?.newFile?.[unifiedItem.newLineNumber ?? -1];
    return !!(oldLineExtend?.data || newLineExtend?.data);
}
function shouldShowSplitExtend(diffFile, index, extendData, hasRenderExtendLine) {
    if (!hasRenderExtendLine)
        return false;
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    const oldLineExtend = extendData?.oldFile?.[oldLine?.lineNumber ?? -1];
    const newLineExtend = extendData?.newFile?.[newLine?.lineNumber ?? -1];
    const hasExtend = !!(oldLineExtend?.data || newLineExtend?.data);
    const enableExpand = diffFile.getExpandEnabled();
    return hasExtend && ((!oldLine?.isHidden && !newLine?.isHidden) || !enableExpand);
}
function shouldShowHunk(diffFile, index, mode) {
    return isSplitDiffMode(mode) ? shouldShowSplitHunk(diffFile, index) : shouldShowUnifiedHunk(diffFile, index);
}
function shouldShowExtend(diffFile, index, options) {
    return isSplitDiffMode(options.mode)
        ? shouldShowSplitExtend(diffFile, index, options.extendData, options.hasRenderExtendLine)
        : shouldShowUnifiedExtend(diffFile, index, options.extendData, options.hasRenderExtendLine);
}
/**
 * Walk the flattened DiffView display sequence (hunk → content → extend per content block).
 * Shared by scroll layout building and rendering.
 */
function iterateDiffDisplayEntries(options) {
    const { diffFile, mode } = options;
    const contentLines = isSplitDiffMode(mode) ? getSplitContentLines(diffFile) : getUnifiedContentLine(diffFile);
    const entries = [];
    contentLines.forEach((item, mapIndex) => {
        if (mapIndex !== 0 && shouldShowHunk(diffFile, item.index, mode)) {
            entries.push({ kind: "hunk", diffIndex: item.index, displayLineNumber: item.lineNumber });
        }
        entries.push({ kind: "content", diffIndex: item.index, displayLineNumber: item.lineNumber });
        if (shouldShowExtend(diffFile, item.index, options)) {
            entries.push({ kind: "extend", diffIndex: item.index, displayLineNumber: item.lineNumber });
        }
    });
    return entries;
}
function getDiffLineNumWidth(diffFile, mode) {
    const lineLength = isSplitDiffMode(mode)
        ? Math.max(diffFile.splitLineLength, diffFile.fileLineLength)
        : Math.max(diffFile.unifiedLineLength, diffFile.fileLineLength);
    return lineLength.toString().length;
}
function getUnifiedContentRowCount(diffFile, index, columns, lineNumWidth) {
    const unifiedLine = diffFile.getUnifiedLine(index);
    if (unifiedLine?.isHidden)
        return 0;
    const rawLine = unifiedLine.value || "";
    const diffLine = unifiedLine.diff;
    const contentWidth = columns - (lineNumWidth + 1) * 2 - 1;
    const contentWithSymbol = diffLine?.changes?.hasLineChange && diffLine.changes.newLineSymbol === NewLineSymbol.NEWLINE
        ? rawLine + noNewlineText
        : rawLine;
    return getCurrentLineRow({ content: contentWithSymbol, width: contentWidth - 2 });
}
function getSplitContentRowCount(diffFile, index, columns, lineNumWidth) {
    const oldLine = diffFile.getSplitLeftLine(index);
    const newLine = diffFile.getSplitRightLine(index);
    if (oldLine?.isHidden && newLine?.isHidden)
        return 0;
    const contentWidth = columns / 2 - lineNumWidth - 2;
    const oldContent = oldLine?.diff?.changes?.hasLineChange && oldLine?.diff?.changes.newLineSymbol === NewLineSymbol.NEWLINE
        ? (oldLine?.value || "") + noNewlineText
        : oldLine?.value || "";
    const newContent = newLine?.diff?.changes?.hasLineChange && newLine?.diff?.changes.newLineSymbol === NewLineSymbol.NEWLINE
        ? (newLine?.value || "") + noNewlineText
        : newLine?.value || "";
    const oldRow = getCurrentLineRow({ content: oldContent, width: contentWidth - 2 });
    const newRow = getCurrentLineRow({ content: newContent, width: contentWidth - 2 });
    return Math.max(oldRow, newRow);
}
function getDiffDisplayEntryRowCount(entry, options) {
    const { diffFile, mode, columns, lineNumWidth, extendLineHeight = 1 } = options;
    switch (entry.kind) {
        case "hunk":
            return 1;
        case "content":
            return isSplitDiffMode(mode)
                ? getSplitContentRowCount(diffFile, entry.diffIndex, columns, lineNumWidth)
                : getUnifiedContentRowCount(diffFile, entry.diffIndex, columns, lineNumWidth);
        case "extend":
            return extendLineHeight;
    }
}
function buildDiffViewScrollLayout(options) {
    if (options.columns <= 0) {
        return { rows: [], lines: [], totalRows: 0, totalLines: 0 };
    }
    const { diffFile, mode, columns, extendLineHeight = 1 } = options;
    const lineNumWidth = getDiffLineNumWidth(diffFile, mode);
    const rows = [];
    const lines = [];
    for (const entry of iterateDiffDisplayEntries(options)) {
        const rowCount = getDiffDisplayEntryRowCount(entry, { diffFile, mode, columns, lineNumWidth, extendLineHeight });
        pushScrollEntry(rows, lines, entry.kind, entry.diffIndex, rowCount);
    }
    return {
        rows,
        lines,
        totalRows: rows.length,
        totalLines: lines.length,
    };
}
function getVisibleDiffScrollLines(layout, scrollOffset, viewportHeight) {
    if (layout.totalRows <= 0)
        return [];
    const viewStart = scrollOffset;
    const viewEnd = scrollOffset + viewportHeight - 1;
    const visible = [];
    for (const entry of layout.lines) {
        if (entry.endRow - 1 < viewStart)
            continue;
        if (entry.startRow > viewEnd)
            break;
        const entryStart = entry.startRow;
        const entryEnd = entry.endRow;
        const visibleStart = Math.max(entryStart, viewStart);
        const visibleEnd = Math.min(entryEnd, viewEnd + 1);
        const rowOffset = visibleStart - entryStart;
        const rowCount = visibleEnd - visibleStart;
        const fullRowCount = entryEnd - entryStart;
        visible.push({
            ...entry,
            clip: rowOffset > 0 || rowCount < fullRowCount ? { rowOffset, rowCount } : undefined,
        });
    }
    return visible;
}
function isSplitDiffMode(mode) {
    return !!(mode & DiffModeEnum.Split);
}
/** @deprecated use isSplitDiffMode */
function getUnifiedLineDisplayNumber(diffFile, diffIndex) {
    const items = getUnifiedContentLine(diffFile);
    const item = items.find((line) => line.index === diffIndex);
    return item?.lineNumber ?? diffIndex + 1;
}
function getSplitLineDisplayNumber(diffFile, diffIndex) {
    const items = getSplitContentLines(diffFile);
    const item = items.find((line) => line.index === diffIndex);
    return item?.lineNumber ?? diffIndex + 1;
}
function getDiffContentLineNumber(diffFile, diffIndex, mode) {
    return isSplitDiffMode(mode)
        ? getSplitLineDisplayNumber(diffFile, diffIndex)
        : getUnifiedLineDisplayNumber(diffFile, diffIndex);
}

function toDisplayDescriptor(entry, diffFile, mode) {
    return {
        kind: entry.kind,
        diffIndex: entry.diffIndex,
        displayLineNumber: getDiffContentLineNumber(diffFile, entry.diffIndex, mode),
    };
}
const DiffDisplayList = memo(({ diffFile, mode, width, extendData, hasRenderExtendLine, visibleEntries, hasFixedHeight, viewportHeight, }) => {
    const { useDiffContext } = useDiffViewContext();
    const { enableHighlight, noBG, themeColors } = useDiffContext.useShallowStableSelector((s) => ({
        enableHighlight: s.enableHighlight,
        noBG: s.noBG,
        themeColors: s.themeColors,
    }));
    useSyncExternalStore(diffFile.subscribe, diffFile.getUpdateCount, diffFile.getUpdateCount);
    const theme = diffFile._getTheme();
    const { columns: terminalColumns } = useTerminalSize();
    const columns = width || terminalColumns || 0;
    const lineNumWidth = getDiffLineNumWidth(diffFile, mode);
    const updateCount = diffFile.getUpdateCount();
    const allEntries = useMemo(() => iterateDiffDisplayEntries({
        diffFile,
        mode,
        extendData,
        hasRenderExtendLine,
    }), 
    // updateCount triggers rebuild when diff content changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diffFile, mode, extendData, hasRenderExtendLine, updateCount]);
    if (!columns)
        return null;
    const isScrollMode = hasFixedHeight && !!visibleEntries;
    const entries = isScrollMode ? visibleEntries : allEntries;
    const list = (jsx(Fragment, { children: entries.map((entry) => {
            const scrollEntry = isScrollMode ? entry : undefined;
            const descriptor = scrollEntry
                ? toDisplayDescriptor(scrollEntry, diffFile, mode)
                : entry;
            const key = scrollEntry
                ? `${descriptor.kind}-${descriptor.diffIndex}-${scrollEntry.lineNumber}-${scrollEntry.clip?.rowOffset ?? 0}`
                : `${descriptor.kind}-${descriptor.diffIndex}-${descriptor.displayLineNumber}`;
            return (jsx(Fragment$1, { children: jsx(DiffDisplayEntry, { entry: descriptor, diffFile: diffFile, mode: mode, theme: theme, columns: columns, lineNumWidth: lineNumWidth, enableHighlight: enableHighlight, noBG: noBG, themeColors: themeColors, scrollSlice: scrollEntry?.clip, entryRowCount: scrollEntry ? scrollEntry.endRow - scrollEntry.startRow : undefined }) }, key));
        }) }));
    if (isScrollMode) {
        return (jsx(Box, { height: viewportHeight, overflow: "hidden", flexDirection: "column", children: list }));
    }
    return list;
});
DiffDisplayList.displayName = "DiffDisplayList";

const EMPTY_SCROLL_LAYOUT = {
    rows: [],
    lines: [],
    totalRows: 0,
    totalLines: 0,
};
function clampScrollOffset(scrollOffset, totalRows, viewportHeight) {
    const maxOffset = Math.max(0, totalRows - viewportHeight);
    return Math.max(0, Math.min(scrollOffset, maxOffset));
}
/**
 * Effective viewport height in visual rows.
 * When `maxHeight` is set, shrinks to content height if content is shorter (avoids trailing blank rows).
 */
function getEffectiveViewportHeight(totalRows, maxHeight) {
    const contentRows = Math.max(totalRows, 1);
    if (maxHeight === undefined)
        return contentRows;
    return Math.min(maxHeight, contentRows);
}
function clampLineNumber(line, totalLines) {
    if (totalLines <= 0)
        return 1;
    return Math.max(1, Math.min(line, totalLines));
}
function findLineLayout(layout, line) {
    return layout.lines.find((item) => item.lineNumber === line);
}
function computeScrollState(layout, scrollOffset, viewportHeight) {
    const totalRows = layout.totalRows;
    const totalLines = layout.totalLines;
    const clampedOffset = clampScrollOffset(scrollOffset, totalRows, viewportHeight);
    const viewStart = clampedOffset;
    const viewEnd = totalRows > 0 ? clampedOffset + viewportHeight - 1 : -1;
    let startLine = totalLines > 0 ? 1 : 0;
    let endLine = totalLines > 0 ? totalLines : 0;
    if (totalLines > 0 && totalRows > 0) {
        for (const line of layout.lines) {
            if (line.endRow - 1 >= viewStart) {
                startLine = line.lineNumber;
                break;
            }
        }
        for (let i = layout.lines.length - 1; i >= 0; i--) {
            const line = layout.lines[i];
            if (line.startRow <= viewEnd) {
                endLine = line.lineNumber;
                break;
            }
        }
    }
    const maxOffset = Math.max(0, totalRows - viewportHeight);
    return {
        totalLines,
        totalRows,
        viewportHeight,
        scrollOffset: clampedOffset,
        startLine,
        endLine,
        canScrollUp: clampedOffset > 0,
        canScrollDown: clampedOffset < maxOffset,
    };
}
function scrollOffsetToTopLine(layout, line, viewportHeight) {
    const targetLine = clampLineNumber(line, layout.totalLines);
    const lineLayout = findLineLayout(layout, targetLine);
    if (!lineLayout)
        return 0;
    return clampScrollOffset(lineLayout.startRow, layout.totalRows, viewportHeight);
}
function scrollOffsetToBottomLine(layout, line, viewportHeight) {
    const targetLine = clampLineNumber(line, layout.totalLines);
    const lineLayout = findLineLayout(layout, targetLine);
    if (!lineLayout)
        return 0;
    return clampScrollOffset(lineLayout.endRow - viewportHeight, layout.totalRows, viewportHeight);
}
function scrollOffsetUp(layout, scrollOffset, viewportHeight, options) {
    const unit = options?.unit ?? "visual";
    const step = options?.step ?? 1;
    if (step <= 0)
        return clampScrollOffset(scrollOffset, layout.totalRows, viewportHeight);
    if (unit === "visual") {
        return clampScrollOffset(scrollOffset - step, layout.totalRows, viewportHeight);
    }
    const state = computeScrollState(layout, scrollOffset, viewportHeight);
    let targetLine = state.startLine;
    for (let i = 0; i < step; i++) {
        const prev = [...layout.lines].reverse().find((line) => line.lineNumber < targetLine);
        if (!prev) {
            return 0;
        }
        targetLine = prev.lineNumber;
    }
    return scrollOffsetToTopLine(layout, targetLine, viewportHeight);
}
function scrollOffsetDown(layout, scrollOffset, viewportHeight, options) {
    const unit = options?.unit ?? "visual";
    const step = options?.step ?? 1;
    if (step <= 0)
        return clampScrollOffset(scrollOffset, layout.totalRows, viewportHeight);
    if (unit === "visual") {
        return clampScrollOffset(scrollOffset + step, layout.totalRows, viewportHeight);
    }
    const state = computeScrollState(layout, scrollOffset, viewportHeight);
    let targetLine = state.startLine;
    for (let i = 0; i < step; i++) {
        const next = layout.lines.find((line) => line.lineNumber > targetLine);
        if (!next) {
            return clampScrollOffset(layout.totalRows - viewportHeight, layout.totalRows, viewportHeight);
        }
        targetLine = next.lineNumber;
    }
    return scrollOffsetToTopLine(layout, targetLine, viewportHeight);
}
function sliceVisibleRows(layout, scrollOffset, viewportHeight) {
    if (layout.rows.length === 0)
        return "";
    const clampedOffset = clampScrollOffset(scrollOffset, layout.totalRows, viewportHeight);
    return layout.rows.slice(clampedOffset, clampedOffset + viewportHeight).join("\n");
}
function createScrollViewRef(layout, scrollOffset, viewportHeight, setScrollOffset) {
    return {
        getScrollState: () => computeScrollState(layout, scrollOffset, viewportHeight),
        scrollToTop: (line) => {
            setScrollOffset(scrollOffsetToTopLine(layout, line, viewportHeight));
        },
        scrollToBottom: (line) => {
            setScrollOffset(scrollOffsetToBottomLine(layout, line, viewportHeight));
        },
        scrollUp: (options) => {
            setScrollOffset(scrollOffsetUp(layout, scrollOffset, viewportHeight, options));
        },
        scrollDown: (options) => {
            setScrollOffset(scrollOffsetDown(layout, scrollOffset, viewportHeight, options));
        },
    };
}
function useScrollView({ layout, height, onScrollChange, resetKey, }) {
    const [scrollOffset, setScrollOffsetState] = useState(0);
    const onScrollChangeRef = useRef(onScrollChange);
    const lastNotifiedStateRef = useRef("");
    const prevResetKeyRef = useRef(undefined);
    useEffect(() => {
        onScrollChangeRef.current = onScrollChange;
    }, [onScrollChange]);
    useEffect(() => {
        if (resetKey === undefined)
            return;
        if (prevResetKeyRef.current !== undefined && prevResetKeyRef.current !== resetKey) {
            setScrollOffsetState(0);
            lastNotifiedStateRef.current = "";
        }
        prevResetKeyRef.current = resetKey;
    }, [resetKey]);
    const hasFixedHeight = typeof height === "number";
    const viewportHeight = getEffectiveViewportHeight(layout.totalRows, height);
    const effectiveOffset = clampScrollOffset(scrollOffset, layout.totalRows, viewportHeight);
    const setScrollOffset = useCallback((offset) => {
        setScrollOffsetState(offset);
    }, []);
    const scrollState = useMemo(() => computeScrollState(layout, effectiveOffset, viewportHeight), [layout, effectiveOffset, viewportHeight]);
    useEffect(() => {
        const snapshot = JSON.stringify(scrollState);
        if (snapshot === lastNotifiedStateRef.current)
            return;
        lastNotifiedStateRef.current = snapshot;
        onScrollChangeRef.current?.(scrollState);
    }, [scrollState]);
    const visibleOutput = useMemo(() => sliceVisibleRows(layout, effectiveOffset, viewportHeight), [layout, effectiveOffset, viewportHeight]);
    const scrollRef = useMemo(() => createScrollViewRef(layout, effectiveOffset, viewportHeight, setScrollOffset), [layout, effectiveOffset, viewportHeight, setScrollOffset]);
    return {
        scrollState,
        visibleOutput,
        scrollRef,
        viewportHeight,
        hasFixedHeight,
    };
}

_cacheMap.name = "@git-diff-view/cli";
setEnableBuildTemplate(false);
const InternalDiffView = (props) => {
    const { width: _width, diffFile, diffViewMode, diffViewHighlight, wrapperRef, extendData, renderExtendLine, diffViewTabSpace, diffViewTabWidth, diffViewHideOperator, diffViewNoBG, diffViewThemeColors, diffViewExtendLineHeight, height, onScrollChange, forwardedRef, } = props;
    const diffFileId = useMemo(() => diffFile.getId(), [diffFile]);
    const mode = diffViewMode || DiffModeEnum.SplitGitHub;
    // performance optimization
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const useDiffContext = useMemo(() => createDiffConfigStore(props, diffFileId), []);
    useEffect(() => {
        const { id, setId, mode: storeMode, setMode, enableHighlight, setEnableHighlight, setExtendData, renderExtendLine: storeRenderExtendLine, setRenderExtendLine, tabSpace, setTabSpace, tabWidth, setTabWidth, hideOperator, setHideOperator, noBG, setNoBG, setThemeColors, width, setWidth, } = useDiffContext.getReadonlyState();
        if (diffFileId && diffFileId !== id) {
            setId(diffFileId);
        }
        if (mode && mode !== storeMode) {
            setMode(mode);
        }
        if (diffViewHighlight !== enableHighlight) {
            setEnableHighlight(!!diffViewHighlight);
        }
        if (props.extendData) {
            setExtendData(props.extendData);
        }
        if (storeRenderExtendLine !== props.renderExtendLine) {
            setRenderExtendLine(props.renderExtendLine);
        }
        if (diffViewTabSpace !== tabSpace) {
            setTabSpace(!!diffViewTabSpace);
        }
        if (diffViewTabWidth && diffViewTabWidth !== tabWidth) {
            setTabWidth(diffViewTabWidth);
        }
        if (diffViewHideOperator !== hideOperator) {
            setHideOperator(!!diffViewHideOperator);
        }
        if (diffViewNoBG !== noBG) {
            setNoBG(!!diffViewNoBG);
        }
        setThemeColors(diffViewThemeColors);
        if (_width && _width !== width) {
            setWidth(_width);
        }
    }, [
        _width,
        useDiffContext,
        diffViewHighlight,
        mode,
        diffFileId,
        extendData,
        renderExtendLine,
        diffViewTabSpace,
        diffViewTabWidth,
        diffViewHideOperator,
        diffViewNoBG,
        diffViewThemeColors,
        props.extendData,
        props.renderExtendLine,
    ]);
    useEffect(() => {
        const { wrapper, setWrapper } = useDiffContext.getReadonlyState();
        if (wrapperRef.current && wrapperRef.current !== wrapper.current) {
            setWrapper(wrapperRef.current);
        }
    });
    const value = useMemo(() => ({ useDiffContext }), [useDiffContext]);
    return (jsx(DiffViewContext.Provider, { value: value, children: jsx(MemoedDiffViewScrollBody, { diffFile: diffFile, width: _width, mode: mode, height: height, onScrollChange: onScrollChange, forwardedRef: forwardedRef, extendData: props.extendData, diffViewExtendLineHeight: diffViewExtendLineHeight, renderExtendLine: renderExtendLine }) }));
};
const DiffViewScrollBody = ({ diffFile, width: _width, mode, height, onScrollChange, forwardedRef, extendData, diffViewExtendLineHeight, renderExtendLine, }) => {
    const { columns: terminalColumns } = useTerminalSize();
    const columns = _width || terminalColumns || 0;
    const updateCount = useSyncExternalStore(diffFile.subscribe, diffFile.getUpdateCount, diffFile.getUpdateCount);
    const scrollLayout = useMemo(() => {
        if (typeof height !== "number") {
            return EMPTY_DIFF_VIEW_SCROLL_LAYOUT;
        }
        return buildDiffViewScrollLayout({
            diffFile,
            columns,
            mode,
            extendData,
            extendLineHeight: diffViewExtendLineHeight ?? 1,
            hasRenderExtendLine: !!renderExtendLine,
        });
        // updateCount triggers layout rebuild when diff content changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [height, diffFile, columns, mode, extendData, diffViewExtendLineHeight, renderExtendLine, updateCount]);
    const { scrollRef, hasFixedHeight, viewportHeight, scrollState } = useScrollView({
        layout: scrollLayout,
        height,
        onScrollChange,
        resetKey: typeof height === "number" ? `${columns}:${mode}` : undefined,
    });
    const visibleEntries = useMemo(() => {
        if (!hasFixedHeight)
            return undefined;
        return getVisibleDiffScrollLines(scrollLayout, scrollState.scrollOffset, viewportHeight);
    }, [hasFixedHeight, scrollLayout, scrollState.scrollOffset, viewportHeight]);
    useImperativeHandle(forwardedRef, () => ({
        getDiffFileInstance: () => diffFile,
        ...scrollRef,
    }), [diffFile, scrollRef]);
    return (jsx(Box, { "data-component": "git-diff-view", "data-theme": diffFile._getTheme() || "light", "data-version": "0.1.11", flexDirection: "column", "data-highlighter": diffFile._getHighlighterName(), children: jsx(DiffDisplayList, { diffFile: diffFile, mode: mode, width: _width, extendData: extendData, hasRenderExtendLine: !!renderExtendLine, visibleEntries: visibleEntries, hasFixedHeight: hasFixedHeight, viewportHeight: viewportHeight }) }));
};
const MemoedDiffViewScrollBody = memo(DiffViewScrollBody);
const MemoedInternalDiffView = memo(InternalDiffView);
const DiffViewContainerWithRef = (props, ref) => {
    const { registerHighlighter, data, diffViewTheme, diffFile: _diffFile, height, onScrollChange, ...restProps } = props;
    const width = restProps.width;
    const domRef = useRef(null);
    const diffFile = useMemo(() => {
        if (_diffFile) {
            const diffFile = DiffFile.createInstance({});
            diffFile._mergeFullBundle(_diffFile._getFullBundle());
            return diffFile;
        }
        else if (data) {
            return new DiffFile(data?.oldFile?.fileName || "", data?.oldFile?.content || "", data?.newFile?.fileName || "", data?.newFile?.content || "", data?.hunks || [], data?.oldFile?.fileLang || "", data?.newFile?.fileLang || "");
        }
        return null;
    }, [data, _diffFile]);
    const diffFileRef = useRef(diffFile);
    if (diffFileRef.current && diffFileRef.current !== diffFile) {
        diffFileRef.current.clear?.();
        diffFileRef.current = diffFile;
    }
    useEffect(() => {
        if (_diffFile && diffFile) {
            _diffFile._addClonedInstance(diffFile);
            return () => {
                _diffFile._delClonedInstance(diffFile);
            };
        }
    }, [diffFile, _diffFile]);
    useEffect(() => {
        if (!diffFile)
            return;
        diffFile.initTheme(diffViewTheme);
        diffFile.initRaw();
        diffFile.buildSplitDiffLines();
        diffFile.buildUnifiedDiffLines();
    }, [diffFile, diffViewTheme]);
    useEffect(() => {
        if (!diffFile)
            return;
        if (props.diffViewHighlight) {
            diffFile.initSyntax({ registerHighlighter: registerHighlighter });
        }
        diffFile.notifyAll();
    }, [diffFile, props.diffViewHighlight, props.diffViewTheme, registerHighlighter]);
    useUnmount(() => (process.env.NODE_ENV === "development" ? diffFile?._destroy?.() : diffFile?.clear?.()), [diffFile]);
    if (!diffFile)
        return null;
    return (jsx(Box, { ref: domRef, width: width, flexGrow: typeof width === "number" ? undefined : 1, flexShrink: typeof width === "number" ? 0 : undefined, children: jsx(MemoedInternalDiffView, { ...restProps, wrapperRef: domRef, diffFile: diffFile, diffViewTheme: diffViewTheme, diffViewTabWidth: props.diffViewTabWidth || "medium", diffViewMode: restProps.diffViewMode || DiffModeEnum.SplitGitHub, height: height, onScrollChange: onScrollChange, forwardedRef: ref }, diffFile.getId()) }));
};
const InnerDiffView = forwardRef(DiffViewContainerWithRef);
InnerDiffView.displayName = "DiffView";
const DiffView = InnerDiffView;
const version = "0.1.11";

_cacheMap.name = "@git-diff-view/cli";
function buildCodeViewLayout(file, theme, columns, options) {
    const { enableHighlight, noBG, tabSpace, tabWidth, themeColors } = options;
    const maxLineNumber = file.maxLineNumber || 1;
    const lineNumWidth = Math.max(String(maxLineNumber).length, 2);
    const lineNumTotalWidth = lineNumWidth + 2;
    const contentWidth = columns - lineNumTotalWidth - 2;
    if (contentWidth <= 0) {
        return { rows: [], lines: [], totalRows: 0, totalLines: 0 };
    }
    const lineNumBg = noBG
        ? undefined
        : theme === "light"
            ? themeColors.plainLineNumber.light
            : themeColors.plainLineNumber.dark;
    const lineNumColor = theme === "light" ? themeColors.plainLineNumberColor.light : themeColors.plainLineNumberColor.dark;
    const contentBg = noBG
        ? undefined
        : theme === "light"
            ? themeColors.plainContent.light
            : themeColors.plainContent.dark;
    const lineNumStyle = { backgroundColor: lineNumBg, color: lineNumColor, dim: true };
    const padStyle = { backgroundColor: contentBg };
    const totalLines = file.rawLength || 0;
    const rows = [];
    const lines = [];
    for (let lineNumber = 1; lineNumber <= totalLines; lineNumber++) {
        const rawLine = (file.rawFile[lineNumber] || "").replace(/\n$/, "");
        const syntaxLine = file.syntaxFile[lineNumber];
        const baseStyle = { backgroundColor: contentBg };
        let contentChars;
        const useSyntax = enableHighlight && syntaxLine && syntaxLine.nodeList?.length <= 150;
        if (useSyntax) {
            contentChars = [];
            for (const { node, wrapper } of syntaxLine.nodeList || []) {
                const lowlightStyles = getStyleFromClassName(wrapper?.properties?.className?.join(" ") || "");
                const lowlightStyle = theme === "dark" ? lowlightStyles.dark : lowlightStyles.light;
                const shikiStyles = getStyleObjectFromString(wrapper?.properties?.style || "");
                const shikiStyle = theme === "dark" ? shikiStyles.dark : shikiStyles.light;
                const syntaxColor = shikiStyle?.color || lowlightStyle?.color;
                contentChars.push(...processSyntaxCharsForAnsi(node.value.replace(/\n$/, ""), tabSpace, tabWidth, baseStyle, syntaxColor));
            }
        }
        else {
            contentChars = processCharsForAnsi(rawLine, tabSpace, tabWidth, baseStyle);
        }
        const contentRows = splitCharsIntoRows(contentChars, contentWidth, padStyle);
        const pad = styleText(" ", padStyle);
        const startRow = rows.length;
        for (let row = 0; row < contentRows.length; row++) {
            const numText = row === 0 ? ` ${String(lineNumber).padStart(lineNumWidth)} ` : ` ${" ".repeat(lineNumWidth)} `;
            const lineNumStr = styleText(numText, lineNumStyle);
            const contentStr = buildAnsiStringOptimized(contentRows[row]);
            rows.push(`${lineNumStr}${pad}${contentStr}${pad}`);
        }
        lines.push({
            lineNumber,
            startRow,
            endRow: rows.length,
        });
    }
    return {
        rows,
        lines,
        totalRows: rows.length,
        totalLines,
    };
}
function buildCodeViewString(file, theme, columns, options) {
    return buildCodeViewLayout(file, theme, columns, options).rows.join("\n");
}
function buildCodeViewAnsiString(props) {
    const theme = props.codeViewTheme || "light";
    let resolvedFile = null;
    if (props.file) {
        resolvedFile = props.file;
    }
    else if (props.data) {
        resolvedFile = getFile(props.data.content || "", props.data.fileLang || "", theme, props.data.fileName || "");
    }
    if (!resolvedFile)
        return "";
    resolvedFile.doRaw();
    if (props.codeViewHighlight && props.registerHighlighter) {
        resolvedFile.doSyntax({ registerHighlighter: props.registerHighlighter, theme });
    }
    const columns = typeof props.width === "number"
        ? props.width
        : getValidColumns((safeGetProcessColumn() || 60) - TERMINAL_PADDING_X);
    return buildCodeViewString(resolvedFile, theme, columns, {
        enableHighlight: !!props.codeViewHighlight,
        noBG: !!props.codeViewNoBG,
        tabSpace: !!props.codeViewTabSpace,
        tabWidth: props.codeViewTabWidth || "medium",
        themeColors: buildTheme(props.codeViewThemeColors),
    });
}
const InternalCodeView = memo(({ output, theme, hasFixedHeight, viewportHeight, }) => {
    return (jsx(Box, { "data-component": "git-code-view", "data-theme": theme, "data-version": "0.1.11", flexDirection: "column", height: hasFixedHeight ? viewportHeight : undefined, overflow: hasFixedHeight ? "hidden" : undefined, children: jsx(Text, { wrap: "truncate", children: output }) }));
});
InternalCodeView.displayName = "InternalCodeView";
const CodeViewContainerWithRef = (props, ref) => {
    const { registerHighlighter, data, codeViewTheme, file, width, height, onScrollChange, ...restProps } = props;
    const theme = codeViewTheme || "light";
    const { stdout } = useStdout();
    const finalFile = useMemo(() => {
        if (file) {
            file.doRaw();
            if (props.codeViewHighlight) {
                file.doSyntax({ registerHighlighter: registerHighlighter, theme });
            }
            return file;
        }
        if (data) {
            const f = getFile(data.content || "", data.fileLang || "", theme, data.fileName || "");
            f.doRaw();
            if (props.codeViewHighlight) {
                f.doSyntax({ registerHighlighter: registerHighlighter, theme });
            }
            return f;
        }
        return null;
    }, [data, theme, file, props.codeViewHighlight, registerHighlighter]);
    const columns = useMemo(() => {
        if (typeof width === "number")
            return width;
        return getValidColumns((safeGetProcessColumn() || stdout?.columns || 60) - TERMINAL_PADDING_X);
    }, [stdout, width]);
    const options = useMemo(() => ({
        enableHighlight: !!restProps.codeViewHighlight,
        noBG: !!restProps.codeViewNoBG,
        tabSpace: !!restProps.codeViewTabSpace,
        tabWidth: restProps.codeViewTabWidth || "medium",
        themeColors: buildTheme(restProps.codeViewThemeColors),
    }), [
        restProps.codeViewHighlight,
        restProps.codeViewNoBG,
        restProps.codeViewTabSpace,
        restProps.codeViewTabWidth,
        restProps.codeViewThemeColors,
    ]);
    const layout = useMemo(() => {
        if (!finalFile || !columns) {
            return { rows: [], lines: [], totalRows: 0, totalLines: 0 };
        }
        return buildCodeViewLayout(finalFile, theme, columns, options);
    }, [finalFile, theme, columns, options]);
    const { visibleOutput, scrollRef, hasFixedHeight, viewportHeight } = useScrollView({
        layout,
        height,
        onScrollChange,
        resetKey: columns,
    });
    useImperativeHandle(ref, () => ({
        getFileInstance: () => finalFile,
        ...scrollRef,
    }), [finalFile, scrollRef]);
    if (!finalFile || !columns)
        return null;
    const output = hasFixedHeight ? visibleOutput : layout.rows.join("\n");
    return (jsx(Box, { width: width, flexGrow: typeof width === "number" ? undefined : 1, flexShrink: typeof width === "number" ? 0 : undefined, children: jsx(InternalCodeView, { output: output, theme: theme, hasFixedHeight: hasFixedHeight, viewportHeight: viewportHeight }, finalFile.getId()) }));
};
const InnerCodeView = forwardRef(CodeViewContainerWithRef);
InnerCodeView.displayName = "CodeView";
const CodeView = InnerCodeView;

export { CodeView, DiffModeEnum, DiffView, EMPTY_SCROLL_LAYOUT, buildCodeViewAnsiString, buildCodeViewLayout, buildDiffViewScrollLayout, buildTheme, clampLineNumber, clampScrollOffset, computeScrollState, createScrollViewRef, findLineLayout, getDiffDisplayEntryRowCount, getDiffLineNumWidth, getEffectiveViewportHeight, getSplitContentRowCount, getUnifiedContentRowCount, getVisibleDiffScrollLines, iterateDiffDisplayEntries, scrollOffsetDown, scrollOffsetToBottomLine, scrollOffsetToTopLine, scrollOffsetUp, sliceVisibleRows, useScrollView, version };
//# sourceMappingURL=index.mjs.map
